import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { Cohort, getCohortsApi } from 'TenantModule/api/getCohortsApi';
import { cohortQueryKeys } from './cohortQueryKeys';

/**
 * Group-filling picker data (GET /tenant/cohorts). The server orders ACTIVE
 * Enterprise cohorts least-filled first, so the default selection is simply the
 * first entry with room. Short staleTime: capacity moves whenever another
 * operator lands a tenant.
 */
export const useCohortsQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useAppQuery<AxiosResponse<Cohort[]>>({
    queryKey: cohortQueryKeys.list({ identifier: 'useCohortsQuery' }),
    queryFn: ({ token, buildConfig }) =>
      getCohortsApi({ params: undefined, token, config: buildConfig() }),
    staleTime: 30 * 1000,
    enabled,
  });

  const cohorts: Cohort[] = useMemo(() => {
    const raw = query.data?.data;
    return Array.isArray(raw) ? raw : [];
  }, [query.data?.data]);

  return {
    cohorts,
    isLoading: query.isLoading,
    // Distinct from isLoading: with cached data TanStack keeps isLoading false during
    // a background revalidation, so this is the only signal that the list on screen
    // may already be out of date.
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};
